import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Modal,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import api, { API_BASE_URL } from "../../../utils/api";


const WorkOrderVisits = ({ work_order_id, currentUserId }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isCommentsLoading, setIsCommentsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [commentImageURL, setCommentImageURL] = useState(null);
const [openImageModal, setOpenImageModal] = useState(false);
const [isSubmitting, setIsSubmitting] = useState(false);


  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/getEvents`);
        const filteredEvents = response.data.filter(
          (event) => event.work_order_id === Number(work_order_id)
        );
        setEvents(filteredEvents);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };

    if (work_order_id) {
      fetchEvents();
    }
  }, [work_order_id]);

  const fetchComments = async (eventId) => {
    setIsCommentsLoading(true);
    try {
      const response = await api.get(
        `/getComments/${work_order_id}/${eventId}`
      );
      setComments(response.data);
    } catch (error) {
      console.error("Failed to fetch comments:", error);
    } finally {
      setIsCommentsLoading(false);
    }
  };

  const handleOpenComments = (eventId) => {
    setSelectedEventId(eventId);
    setIsModalOpen(true);
    fetchComments(eventId);
  };
  const handleAddComment = async () => {
    if (!newComment.trim()) {
      alert("Comment cannot be empty.");
      return;
    }
  
    setIsSubmitting(true); // ✅ Prevent multiple clicks
  
    try {
      const formData = new FormData();
      formData.append("work_order_id", work_order_id);
      formData.append("event_id", selectedEventId);
      formData.append("user_id", currentUserId);
      formData.append("comment_text", newComment);
      formData.append("is_internal", false);
      if (selectedFile) formData.append("upload_urls", selectedFile);
  
      await api.post(`/addComment`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
  
      setComments((prevComments) => [
        {
          comment_text: newComment,
          sent_at: new Date().toISOString(),
          user_name: "You",
          user_lastname: "",
        },
        ...prevComments,
      ]);
  
      setNewComment("");
      setSelectedFile(null);
      setFilePreview(null);
      setIsModalOpen(false);
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to add comment:", error);
      alert("Failed to add comment. Please try again.");
    } finally {
      setIsSubmitting(false); // ✅ Re-enable button
    }
  };
  

  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/*",
    multiple: false,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        setSelectedFile(file);
        setFilePreview(URL.createObjectURL(file));
      }
    },
  });

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Visits Related to Work Order ID: {work_order_id}
      </Typography>
      {loading ? (
        <Typography>Loading...</Typography>
      ) : events.length === 0 ? (
        <Typography>No visits found.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Work Order ID</TableCell>
                <TableCell>Event ID</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Start Time</TableCell>
                <TableCell>End Time</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((event) => (
                <TableRow key={event.id}>
                  <TableCell>{event.work_order_id}</TableCell>
                  <TableCell>{event.id}</TableCell>
                  <TableCell>{event.title}</TableCell>
                  <TableCell>{event.description}</TableCell>
                  <TableCell>
                    {new Date(event.start_time).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {new Date(event.end_time).toLocaleString()}
                  </TableCell>
                  <TableCell>{event.created_by_user_id}</TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => handleOpenComments(event.id)}
                    >
                      View/Add Comments
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Modal for Viewing and Adding Comments */}
      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        aria-labelledby="comments-modal"
      >
        <Box
  sx={{
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 500,
    maxHeight: "80vh",
    overflowY: "auto",
    backgroundColor: "white",
    padding: 4,
    borderRadius: 2,
    boxShadow: 24,
  }}
>

          <Typography id="comments-modal" variant="h6" sx={{ mb: 2 }}>
            Comments for Work Order ID: {work_order_id}, Event ID: {selectedEventId}
          </Typography>

          {isCommentsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <CircularProgress />
            </Box>
          ) : comments.length > 0 ? (
            comments.map((comment, index) => (
              <Box
                key={index}
                sx={{
                  mb: 2,
                  p: 2,
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                }}
              >
                <Typography variant="body2" sx={{ mb: comment.upload_urls ? 1 : 2 }}>
  {comment.comment_text}
</Typography>

{comment.upload_urls && (
        <Box
          sx={{ textAlign: "left", mb: 2, cursor: "pointer" }}
          onClick={() => {
            setCommentImageURL(`${API_BASE_URL}/workordercommentimages/${encodeURIComponent(comment.upload_urls)}`);
            setOpenImageModal(true);
          }}
        >
          <img
            src={`${API_BASE_URL}/workordercommentimages/${encodeURIComponent(comment.upload_urls)}`}
            alt="Attachment"
            style={{
              maxWidth: "100%",
              maxHeight: 150,
              borderRadius: 8,
              border: "1px solid #ccc",
            }}
          />
          </Box>
      )}

      <Typography variant="caption" color="textSecondary">
        Added by {comment.user_name} {comment.user_lastname || ""} on{" "}
        {new Date(comment.sent_at).toLocaleString()}
      </Typography>
    </Box>
  ))
) : (
  <Typography>No comments available.</Typography>
)}

          <TextField
            fullWidth
            label="Add a Comment"
            multiline
            rows={3}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            sx={{ mt: 2, mb: 2 }}
          />

          <Box
            {...getRootProps()}
            sx={{
              border: "2px dashed #ccc",
              borderRadius: "8px",
              padding: "20px",
              textAlign: "center",
              cursor: "pointer",
              mb: 2,
            }}
          >
            <input {...getInputProps()} />
            <Typography>Drag & drop an image, or click to select</Typography>
          </Box>

          {filePreview && (
            <Box sx={{ textAlign: "center", mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                📎 Preview:
              </Typography>
              <img
                src={filePreview}
                alt="Preview"
                style={{ maxWidth: "100%", maxHeight: 150 }}
              />
            </Box>
          )}

<Button
  fullWidth
  variant="contained"
  color="primary"
  onClick={handleAddComment}
  disabled={isSubmitting} 
>
  {isSubmitting ? (
    <CircularProgress size={24} color="inherit" />
  ) : (
    "Save Comment"
  )}
</Button>

        </Box>
      </Modal>

      {/* ✅ Snackbar for confirmation */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          ✅ Comment added successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkOrderVisits;
